import { queryOptions } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import {
  TEAM_ERROR_LABELS,
  type AgendaAwaitingSchedule,
  type AgendaEvents,
  type AgendaEventType,
  type AgendaTicketSearchItem,
  type Calendar,
} from '@erp/shared';

export function calendarsOptions() {
  return queryOptions({
    queryKey: ['erp', 'agenda', 'calendars'] as const,
    queryFn: async ({ signal }) => {
      const res = await api<Calendar[]>('/agenda/calendars', { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

export interface AgendaEventsRange {
  start: string;
  end: string;
  /** spec 039: só estes atendentes (a página inicial pede só os meus) */
  userIds?: number[];
  /** spec 039: só estes tipos; sem, vêm os três */
  types?: AgendaEventType[];
  /** adendo da 039: também os compromissos de tickets ocultos */
  includeHidden?: boolean;
}

export function agendaEventsOptions(range: AgendaEventsRange) {
  return queryOptions({
    queryKey: ['erp', 'agenda', 'events', range] as const,
    queryFn: async ({ signal }) => {
      const res = await api<AgendaEvents>('/agenda/events', {
        query: {
          start: range.start,
          end: range.end,
          user_ids: range.userIds,
          types: range.types?.join(','),
          include_hidden: range.includeHidden ? 1 : undefined,
        },
        signal,
      });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

/**
 * A busca do "Agendar" (adendo da 039): a leitura própria da agenda, com o
 * casamento da lista de tickets (spec 012) e, com o switch, os tickets
 * ocultos — que a lista geral não mostra. Abertos primeiro, até 20.
 */
export function agendaTicketsQuery(params: { search: string; includeHidden: boolean }) {
  return queryOptions({
    queryKey: ['erp', 'agenda', 'tickets', params] as const,
    queryFn: async ({ signal }) => {
      const res = await api<AgendaTicketSearchItem[]>('/agenda/tickets', {
        query: { search: params.search, include_hidden: params.includeHidden ? 1 : undefined },
        signal,
      });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

/**
 * A lista "Aguardando data" (adendo 2 da 039): os tickets em etapa de
 * agendamento sem compromisso em aberto, com o período do bot. Segue o switch
 * de ocultos da aba. A chave fica SOB a da agenda (`['erp', 'agenda']`): o
 * agendamento invalida a agenda inteira, e o ticket agendado sai da lista
 * junto com a entrada dele no calendário.
 */
export function awaitingScheduleQuery(params: { includeHidden: boolean }) {
  return queryOptions({
    queryKey: ['erp', 'agenda', 'awaiting-schedule', params] as const,
    queryFn: async ({ signal }) => {
      const res = await api<AgendaAwaitingSchedule>('/agenda/awaiting-schedule', {
        query: { include_hidden: params.includeHidden ? 1 : undefined },
        signal,
      });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

export function errorMessage(err: unknown): string {
  if (
    err !== null &&
    typeof err === 'object' &&
    'error' in err &&
    typeof (err as { error: unknown }).error === 'string'
  ) {
    const code = (err as { error: string }).error;
    // pessoa fora da equipe (pedido da PoolTrack, 05/10); o resto segue cru
    return TEAM_ERROR_LABELS[code] ?? code;
  }
  return err instanceof Error ? err.message : 'Erro inesperado';
}

/** Código de erro da API (ex.: 'calendar_in_use') sem a mensagem formatada. */
export function errorCode(err: unknown): string | null {
  if (
    err !== null &&
    typeof err === 'object' &&
    'error' in err &&
    typeof (err as { error: unknown }).error === 'string'
  ) {
    return (err as { error: string }).error;
  }
  return null;
}
